<template>
    <transition
        :name="transitionName"
        appear
        v-on="$attrs">
        <div
            v-if="isModalVisible"
            ref="modal"
            v-esc-keyup="escKeyupHandler"
            class="sbs-modal trs"
            :class="modalClasses">
            <div
                v-if="showOverlay"
                class="sbs-modal__overlay"
                :class="modalOverlayClass"
                @click="clickOutsideHandler"></div>
            <div
                class="sbs-modal__content"
                :class="modalContentClass"
                :style="dimensionStyles">
                <SbsBaseButton
                    v-if="showCloseButton"
                    class="sbs-modal__close-button trs"
                    @click="hide">
                    <!-- @slot Слот для крестика -->
                    <slot name="closeButton">
                        <SbsIcon
                            class="trs"
                            v-bind="closeButtonData" />
                    </slot>
                </SbsBaseButton>

                <div
                    ref="modalBody"
                    class="sbs-modal__body custom-scrollbar"
                    :class="modalBodyClass">
                    <!-- @slot Слот по-умолчанию -->
                    <slot></slot>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import {
    disableBodyScroll,
    enableBodyScroll,
} from 'body-scroll-lock/lib/bodyScrollLock.esm.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import dimensionsMixin from '../../mixins/dimensions.js';
import {
    converToUnit,
    getScrollbarWidth,
} from '../../utils/helpers.js';
import {
    AllPositions,
    APP_POSITIONS,
} from '../../utils/positions.js';
import { isArrayContainsValue } from '../../utils/validators.js';
import { AllColors } from '../../utils/colors.js';
import {
    AllTransitions,
    APP_TRANSITIONS,
} from '../../utils/transitions.js';
import { AllSizes } from '../../utils/sizes.js';
import { AllIconGroups } from '../icon/utils/consts.js';
import {
    AllModalTypes,
    MODAL_TYPES,
} from './utils/consts.js';

/**
 * Компонент модального окна
 */
export default {
    name: 'SbsModal',
    emits: ['update:modelValue'],
    mixins: [dimensionsMixin],
    data() {
        return {
            parentContainer: null,
            isModalVisible: false,
            isScrollVisible: false,
            resizeObserver: null,
            vw: window.innerWidth,
            vh: window.innerHeight,
        };
    },
    computed: {
        isSidebar() {
            return this.type === AllModalTypes.SIDEBAR;
        },
        modalClasses() {
            return [
                `sbs-modal--${this.position}`,
                {
                    'sbs-modal--sidebar': this.isSidebar,
                    'persistent': !this.closeOnClickOutside,
                    'opened': this.isModalVisible,
                    'scroll-visible': this.isScrollVisible,
                },
            ];
        },
        closeButtonData() {
            return {
                width: 25,
                height: 25,
                iconName: 'close',
                iconGroup: AllIconGroups.LIGHT,
                iconSize: AllSizes.X_LARGE,
                colorTheme: AllColors.GRAY,
                hoverColor: AllColors.BLACK,
            };
        },
    },
    methods: {
        setupContainer() {
            this.parentContainer = document.querySelector('.sbs-modal-container');

            if (this.parentContainer) return;

            this.parentContainer = document.createElement('div');
            this.parentContainer.classList.add('sbs-modal-container');
            document.body.appendChild(this.parentContainer);
        },
        init() {
            this.setScrollbarWidth();

            if (this.$refs.modalBody) {
                this.resizeObserver.observe(this.$refs.modalBody);
            }

            this.parentContainer.appendChild(this.$refs.modal);
        },
        async show() {
            window.addEventListener('resize', this.updateWindowInnerSizes);

            this.updateWindowInnerSizes();
            this.isModalVisible = true;

            await this.$nextTick();

            if (this.bodyScrollLock) {
                disableBodyScroll(
                    this.$refs.modalBody,
                    { reserveScrollBarGap: true },
                );
            }

            this.calcScroll();
            this.init();
        },
        hide() {
            if (this.bodyScrollLock && this.$refs.modalBody) {
                enableBodyScroll(this.$refs.modalBody);
            }

            this.isModalVisible = false;
            this.$emit('update:modelValue', false);

            window.removeEventListener('resize', this.updateWindowInnerSizes);
        },
        clickOutsideHandler() {
            if (this.closeOnClickOutside) this.hide();
        },
        escKeyupHandler() {
            if (this.closeOnEscPress) this.hide();
        },
        setScrollbarWidth() {
            const scrollbarWidth = getScrollbarWidth(this.$refs.modalBody);

            this.$el.style.setProperty(
                '--modal-scrollbar-width',
                converToUnit(scrollbarWidth),
            );
        },
        calcIsScrollVisible() {
            const {
                offsetHeight,
                scrollHeight,
            } = this.$refs.modalBody;

            this.isScrollVisible = scrollHeight > offsetHeight;
        },
        async calcScroll() {
            await this.$nextTick();

            if (!this.isModalVisible) return;

            this.setScrollbarWidth();
            this.calcIsScrollVisible();
        },
        getWindowInnerWidth() {
            this.vw = window.innerWidth;
        },
        getWindowInnerHeight() {
            this.vh = window.innerHeight;
        },
        setGlobalViewportVariables() {
            document.documentElement.style.setProperty(
                '--vw',
                converToUnit(this.vw),
            );
            document.documentElement.style.setProperty(
                '--vh',
                converToUnit(this.vh),
            );
        },
        updateWindowInnerSizes() {
            this.getWindowInnerWidth();
            this.getWindowInnerHeight();
            this.setGlobalViewportVariables();
        },
    },
    props: {
        /**
         * Блокировать скролл на `body`
         */
        bodyScrollLock: {
            type: Boolean,
            default: true,
        },
        /**
         * Должно ли модальное окно закрываться по клику вне его области
         */
        closeOnClickOutside: {
            type: Boolean,
            default: true,
        },
        /**
         * Должно ли модальное окно закрываться по нажатию на клавишу Escape
         */
        closeOnEscPress: {
            type: Boolean,
            default: true,
        },
        /**
         * Класс для контейнера с содержимым модального окна
         */
        modalBodyClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для контейнера с контентом модального окна
         */
        modalContentClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для контейнера с затемнением фона модального окна
         */
        modalOverlayClass: {
            type: String,
            default: '',
        },
        /**
         * Позиционирование модального окна относительно окна браузера
         *
         * Валидны следующие значения позиции: `top`, `right`, `bottom`, `left`, `center`
         *
         * Также можно указать модификаторы для выравнивания
         * относительно края: `-start`, `-end`
         *
         * Примеры использования: `top`, `left-start`, `bottom-end`
         */
        position: {
            type: String,
            default: AllPositions.CENTER,
            validator: isArrayContainsValue(APP_POSITIONS),
        },
        /**
         * Отображение кнопки закрытия
         */
        showCloseButton: {
            type: Boolean,
            default: true,
        },
        /**
         * Определяет будет ли виден полупрозрачный фон
         */
        showOverlay: {
            type: Boolean,
            default: true,
        },
        /**
         * Название класса для анимации открытия/закрытия
         */
        transitionName: {
            type: String,
            default: AllTransitions.FADE,
            validator: isArrayContainsValue(APP_TRANSITIONS),
        },
        /**
         * Определяет тип модального окна:
         *  - модальное окно, выровненное по центру экрана (по-умолчанию)
         *  - боковая панель, выровненная по правому краю экрана
         */
        type: {
            type: String,
            default: AllModalTypes.MODAL,
            validator: isArrayContainsValue(MODAL_TYPES),
        },
        /**
         * Определяет, является ли компонент видимым или скрытым.
         * @model
         */
        modelValue: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        modelValue(val) {
            if (val) {
                this.show();
            } else if (this.isModalVisible) {
                this.hide();
            }
        },
    },
    created() {
        if (this.isSidebar) {
            const sidebarValidPositions = [
                AllPositions.LEFT,
                AllPositions.RIGHT,
            ];

            if (!isArrayContainsValue(sidebarValidPositions)(this.position)) {
                console.warn(`[Sbs ui kit plugin]: SbsModal: Invalid value '${this.position}' for prop 'position' with type 'sidebar'. Valid: '${sidebarValidPositions.join('\', \'')}'.`);
            }
        }

        this.resizeObserver = new ResizeObserver(this.calcScroll);
    },
    beforeMount() {
        this.setupContainer();
        this.isModalVisible = this.modelValue;
    },
    async mounted() {
        if (this.isModalVisible) {
            await this.show();
        }
    },
    beforeUnmount() {
        this.hide();

        if (this.$refs.modal) {
            this.$refs.modal.remove();
        }

        this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.updateWindowInnerSizes);
    },
};
</script>

<style lang="scss">
.sbs-modal-container {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    width: var(--vw, 100vw);
    height: var(--vh, 100vh);
    overflow: hidden;
    pointer-events: none;
}

.sbs-modal {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    pointer-events: none;

    &--top,
    &--top-start,
    &--top-end,
    &--left-start,
    &--right-start {
        justify-content: flex-start;
    }

    &--bottom,
    &--bottom-start,
    &--bottom-end,
    &--left-end,
    &--right-end {
        justify-content: flex-end;
    }

    &--top-start,
    &--bottom-start,
    &--left-start,
    &--left,
    &--left-end {
        align-items: flex-start;
    }

    &--top-end,
    &--bottom-end,
    &--right-start,
    &--right,
    &--right-end {
        align-items: flex-end;
    }

    &--sidebar {
        flex-direction: row;
        align-items: stretch;
        justify-content: flex-end;

        &.sbs-modal--left-start,
        &.sbs-modal--left,
        &.sbs-modal--left-end {
            justify-content: flex-start;
        }

        &.sbs-modal--left-start,
        &.sbs-modal--right-start {
            align-items: flex-start;
        }

        &.sbs-modal--left-end,
        &.sbs-modal--right-end {
            align-items: flex-end;
        }
    }

    &.opened {
        pointer-events: all;
    }

    &__overlay {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 0;
        background-color: $black;
        cursor: pointer;
        opacity: 0.3;
        pointer-events: none;

        .sbs-modal.opened & {
            pointer-events: all;
        }

        .sbs-modal.persistent & {
            cursor: default;
        }
    }

    &__content {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        max-width: 100vw;
        max-height: 100vh;
        overflow: hidden;
        border-radius: $rounded-15;

        .sbs-modal--sidebar & {
            border-radius: $rounded-0;
        }
    }

    &__close-button {
        position: absolute;
        top: 25px;
        right: 25px;
        z-index: 10;
        display: flex;
        font-size: 20px;

        &::before {
            position: absolute;
            top: 50%;
            left: 50%;
            z-index: -1;
            width: 800%;
            height: 800%;
            background-image:
                radial-gradient(
                    50% 50% at 50% 50%,
                    $white 43.75%,
                    rgba($white, 0) 100%
                );
            border-radius: $rounded-circle;
            transform: translate(-50%, -50%);
            pointer-events: none;

            .sbs-modal.scroll-visible & {
                content: "";
            }
        }
    }

    &__body {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        padding: 40px 50px;
        overflow-x: hidden;
        overflow-y: auto;
        background-color: $white;
        -webkit-overflow-scrolling: touch;
    }
}
</style>
